import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import './Navbar.css';
import logo from './logo.svg';
import { useNavigate } from 'react-router-dom';

const appStoreLinks = [
  {
    alt: 'Download on Google Play',
    src: 'assets/images/banners/gp.webp',
    onClick: () =>
      window.open(
        'https://play.google.com/store/apps/details?id=no.digiberry.lunchapp&gl=NO',
        '_blank',
        'noopener,noreferrer'
      ),
  },
  {
    alt: 'Download on the App Store',
    src: 'assets/images/banners/as.webp',
    onClick: () =>
      window.open(
        'https://apps.apple.com/us/app/lunsj-p%C3%A5-d%C3%B8ra/id1617445017',
        '_blank',
        'noopener,noreferrer'
      ),
  },
];

const primaryLinks = [];

export const Navbar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const loginActions = useMemo(
    () => ({
      admin: () => window.open(process.env.REACT_APP_ADMIN_PORTAL_URL, '_blank', 'noopener,noreferrer'),
      planner: () => window.open(process.env.REACT_APP_MEAL_PLANNER_PORTAL_URL, '_blank', 'noopener,noreferrer'),
    }),
    [navigate]
  );

  const navLinkProps = (href) => ({
    component: 'a',
    href,
    onClick: () => setDrawerOpen(false),
  });

  return (
    <>
      <AppBar
        position='sticky'
        color='transparent'
        elevation={0}
        sx={{
          backdropFilter: 'blur(14px)',
          backgroundColor: 'rgba(255,255,255,0.92)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth='lg'>
          <Toolbar disableGutters sx={{ minHeight: { xs: 72, md: 90 } }}>
            <Stack
              direction='row'
              alignItems='center'
              spacing={1.5}
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <Box component='img' src={logo} alt='FoodFe logo' sx={{ height: 48, width: 'auto' }} />
            </Stack>

            {primaryLinks.length ? (
              <Stack
                direction='row'
                spacing={3}
                sx={{
                  flexGrow: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: { xs: 'none', md: 'flex' },
                }}
              >
                {primaryLinks.map((link) => (
                  <Button
                    key={link.label}
                    {...navLinkProps(link.href)}
                    variant='text'
                    color='inherit'
                    sx={{
                      fontSize: '1.5rem',
                      fontWeight: 500,
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'text.primary',
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
              </Stack>
            ) : (
              <Box sx={{ flexGrow: 1 }} />
            )}

            <Stack
              direction='row'
              spacing={2}
              alignItems='center'
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              <Stack direction='row' spacing={1} alignItems='center'>
                <Box
                  component={PhoneRoundedIcon}
                  sx={{ color: 'primary.main', fontSize: '2rem' }}
                />
                <Typography variant='subtitle1' color='text.primary'>
                  +47 41 16 94 60
                </Typography>
              </Stack>
              <Button variant='outlined' color='primary' onClick={loginActions.admin}>
                Admin Login
              </Button>
              <Button
                variant='contained'
                color='secondary'
                endIcon={<ArrowOutwardRoundedIcon fontSize='small' />}
                onClick={loginActions.planner}
              >
                Menu Planner
              </Button>
            </Stack>

            <IconButton
              color='inherit'
              aria-label='open navigation'
              sx={{ display: { xs: 'inline-flex', md: 'none' }, ml: 'auto' }}
              onClick={toggleDrawer(true)}
            >
              <MenuRoundedIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor='right'
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: '85vw',
            maxWidth: 360,
            paddingTop: 2,
            backgroundImage: 'none',
          },
        }}
      >
        <Stack direction='row' alignItems='center' justifyContent='space-between' px={2} py={1}>
          <Box component='img' src={logo} alt='FoodFe logo' sx={{ height: 36, width: 'auto' }} />
          <IconButton onClick={toggleDrawer(false)}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        {primaryLinks.length ? (
          <>
            <List>
              {primaryLinks.map((link) => (
                <ListItemButton key={link.label} {...navLinkProps(link.href)}>
                  <ListItemText
                    primary={link.label}
                    primaryTypographyProps={{ fontSize: '1.6rem', fontWeight: 500 }}
                  />
                </ListItemButton>
              ))}
            </List>
            <Divider sx={{ my: 1 }} />
          </>
        ) : null}
        <Stack spacing={2} px={2} pb={3}>
          <Button variant='contained' color='secondary' onClick={loginActions.planner}>
            Open Menu Planner
          </Button>
          <Button variant='outlined' onClick={loginActions.admin}>
            Admin Login
          </Button>
          <Stack direction='row' spacing={1.5} alignItems='center' pt={1}>
            <PhoneRoundedIcon color='primary' />
            <Typography variant='subtitle1' color='text.primary'>
              +47 41 16 94 60
            </Typography>
          </Stack>
          <Divider flexItem sx={{ my: 1 }} />
          <Stack direction='row' spacing={2}>
            {appStoreLinks.map((item) => (
              <Box
                key={item.alt}
                component='img'
                src={item.src}
                alt={item.alt}
                sx={{ height: 32, width: 'auto', cursor: 'pointer' }}
                onClick={() => {
                  item.onClick();
                  setDrawerOpen(false);
                }}
              />
            ))}
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
};
